import React, { useState } from 'react';
import { Button, Input, Toast } from '../components/common';
import { useAuth } from '../context/AuthContext';
import authService from '../services/auth.service';
import './Settings.css';

const Settings: React.FC = () => {
  const { user, logout } = useAuth();
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [errors, setErrors] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [toast, setToast] = useState({
    isVisible: false,
    message: '',
    type: 'success' as 'success' | 'error'
  });

  const validatePassword = (): boolean => {
    const newErrors = {
      currentPassword: '',
      newPassword: '',
      confirmPassword: ''
    };
    let isValid = true;

    if (!passwordData.currentPassword) {
      newErrors.currentPassword = 'La contraseña actual es requerida';
      isValid = false;
    }

    if (!passwordData.newPassword) {
      newErrors.newPassword = 'La nueva contraseña es requerida';
      isValid = false;
    } else if (passwordData.newPassword.length < 8) {
      newErrors.newPassword = 'La contraseña debe tener al menos 8 caracteres';
      isValid = false;
    } else if (!/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/.test(passwordData.newPassword)) {
      newErrors.newPassword = 'Debe contener mayúsculas, minúsculas y números';
      isValid = false;
    }

    if (!passwordData.confirmPassword) {
      newErrors.confirmPassword = 'Confirma la nueva contraseña';
      isValid = false;
    } else if (passwordData.newPassword !== passwordData.confirmPassword) {
      newErrors.confirmPassword = 'Las contraseñas no coinciden';
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  };

  const handlePasswordChange = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validatePassword()) return;

    setIsChangingPassword(true);

    try {
      const result = await authService.changePassword(passwordData);

      if (result.success) {
        setToast({
          isVisible: true,
          message: 'Contraseña actualizada. Inicia sesión nuevamente.',
          type: 'success'
        });
        setPasswordData({
          currentPassword: '',
          newPassword: '',
          confirmPassword: ''
        });
        // Cerrar sesión después de cambiar contraseña
        setTimeout(() => {
          logout();
        }, 2000);
      }
    } catch (error: any) {
      setToast({
        isVisible: true,
        message: error.message || 'Error al cambiar contraseña',
        type: 'error'
      });
    } finally {
      setIsChangingPassword(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setPasswordData(prev => ({ ...prev, [name]: value }));
    if (errors[name as keyof typeof errors]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  return (
    <div className="settings-page">
      <Toast
        message={toast.message}
        type={toast.type}
        isVisible={toast.isVisible}
        onClose={() => setToast(prev => ({ ...prev, isVisible: false }))}
      />

      {/* Profile Section */}
      <div className="settings-card">
        <div className="card-header">
          <div className="header-icon">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
              <circle cx="12" cy="7" r="4" />
            </svg>
          </div>
          <div>
            <h2>Información del Perfil</h2>
            <p>Datos de tu cuenta (solo lectura)</p>
          </div>
        </div>

        <div className="profile-info">
          <div className="profile-avatar">
            {user?.name?.charAt(0).toUpperCase() || 'U'}
          </div>
          <div className="profile-details">
            <div className="detail-row">
              <span className="detail-label">Nombre</span>
              <span className="detail-value">{user?.name || 'Usuario'}</span>
            </div>
            {user?.username && (
              <div className="detail-row">
                <span className="detail-label">Username</span>
                <span className="detail-value username-value">@{user.username}</span>
              </div>
            )}
            <div className="detail-row">
              <span className="detail-label">Email</span>
              <span className="detail-value">{user?.email || 'N/A'}</span>
            </div>
            {user?.phone && (
              <div className="detail-row">
                <span className="detail-label">Teléfono</span>
                <span className="detail-value">{user.phone}</span>
              </div>
            )}
            <div className="detail-row">
              <span className="detail-label">Rol</span>
              <span className="detail-value role-badge">
                {user?.role === 'admin' ? 'Administrador' : 'Usuario'}
              </span>
            </div>
          </div>
        </div>

        {/* Credenciales de acceso */}
        <div className="login-credentials-info">
          <h3>Credenciales de Acceso</h3>
          <p className="credentials-subtitle">Puedes iniciar sesión con cualquiera de estas opciones:</p>
          <ul className="credentials-list">
            {user?.username && (
              <li>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="10" />
                  <path d="M12 8v4l2 2" />
                </svg>
                <span><strong>Username:</strong> @{user.username}</span>
              </li>
            )}
            {user?.email && (
              <li>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                  <polyline points="22,6 12,13 2,6" />
                </svg>
                <span><strong>Email:</strong> {user.email}</span>
              </li>
            )}
            {user?.phone && (
              <li>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07 19.5 19.5 0 01-6-6 19.79 19.79 0 01-3.07-8.67A2 2 0 014.11 2h3a2 2 0 012 1.72 12.84 12.84 0 00.7 2.81 2 2 0 01-.45 2.11L8.09 9.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45 12.84 12.84 0 002.81.7A2 2 0 0122 16.92z" />
                </svg>
                <span><strong>Teléfono:</strong> {user.phone}</span>
              </li>
            )}
          </ul>
        </div>
      </div>

      {/* Password Section */}
      <div className="settings-card">
        <div className="card-header">
          <div className="header-icon">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
              <path d="M7 11V7a5 5 0 0 1 10 0v4" />
            </svg>
          </div>
          <div>
            <h2>Cambiar Contraseña</h2>
            <p>Actualiza tu contraseña de acceso</p>
          </div>
        </div>

        <form className="password-form" onSubmit={handlePasswordChange}>
          <Input
            label="Contraseña actual"
            type="password"
            name="currentPassword"
            value={passwordData.currentPassword}
            onChange={handleInputChange}
            error={errors.currentPassword}
            placeholder="Ingresa tu contraseña actual"
          />

          <Input
            label="Nueva contraseña"
            type="password"
            name="newPassword"
            value={passwordData.newPassword}
            onChange={handleInputChange}
            error={errors.newPassword}
            placeholder="Mínimo 8 caracteres"
            helperText="Debe contener mayúsculas, minúsculas y números"
          />

          <Input
            label="Confirmar nueva contraseña"
            type="password"
            name="confirmPassword"
            value={passwordData.confirmPassword}
            onChange={handleInputChange}
            error={errors.confirmPassword}
            placeholder="Repite la nueva contraseña"
          />

          <div className="form-actions">
            <Button
              type="submit"
              variant="primary"
              isLoading={isChangingPassword}
            >
              Actualizar Contraseña
            </Button>
          </div>
        </form>
      </div>

      {/* Security Tips */}
      <div className="settings-card tips-card">
        <h3>Consejos de Seguridad</h3>
        <ul className="tips-list">
          <li>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
            </svg>
            <span>Usa una contraseña única que no uses en otros sitios</span>
          </li>
          <li>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
            </svg>
            <span>Combina letras mayúsculas, minúsculas, números y símbolos</span>
          </li>
          <li>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
            </svg>
            <span>Cambia tu contraseña periódicamente</span>
          </li>
          <li>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
            </svg>
            <span>No compartas tu contraseña con nadie</span>
          </li>
        </ul>
      </div>
    </div>
  );
};

export default Settings;
